import React, { useState, useEffect, useMemo, useRef } from 'react';
import { createPortal } from 'react-dom';
import { SopAPI } from '../../../utils/api';
import { useAuth } from '../../AuthProvider';
import { isAdminOnlyDoc } from '../../../utils/docTags';
import { isDocsMetaDoc } from '../../../utils/docOrganization';
import { mentionIconText } from '../utils/mentionUtils';
import { clampFloatingMenuPosition } from '../utils/floatingMenuPosition';
import { usePickerDocs } from '../hooks/usePickerDocs';

/**
 * DocMentionPicker — opened by the "/doc" slash command (Chunks 10a/10b/14).
 * Searches the workspace's REAL Docs library by title or tag; picking one
 * drops an inline mention tag into the paragraph.
 *
 * Speed (chunk 14): when the host already holds the library metadata it
 * passes it via `availableDocs` and the picker opens INSTANTLY. Otherwise
 * the shared catalog loads once when the picker opens.
 *
 * CREATE IN PLACE: the bottom row always shows "Create New: …" as a hint.
 * Typing a name in the search field fills it in; Enter or click creates only
 * when the name is non-empty and not already in the library.
 *
 * Unlike the slash menu (which filters as you type in the block), this owns
 * a search input: the caret's spot in the paragraph was captured before
 * focus moved here and is restored on insert.
 */
export const DocMentionPicker = ({ position, onPick, onClose, availableDocs = null }) => {
  // Doc-level permission: user-tier members never see admin-only docs here
  const { role } = useAuth();
  const canViewAdminDocs = (role ?? 'admin') !== 'user';

  const { docs, loadError } = usePickerDocs(availableDocs);
  const [query, setQuery] = useState('');
  const [selectedIndex, setSelectedIndex] = useState(0);
  const [adjustedPosition, setAdjustedPosition] = useState(position);
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState('');
  const menuRef = useRef(null);
  const inputRef = useRef(null);

  const results = useMemo(() => {
    if (!docs) return [];
    const permitted = canViewAdminDocs
      ? docs
      : docs.filter((doc) => !isAdminOnlyDoc(doc));
    const active = permitted.filter((doc) => !doc.deletedAt && !isDocsMetaDoc(doc));
    const q = query.trim().toLowerCase();
    if (!q) return active;
    return active.filter(
      (doc) =>
        (doc.title || '').toLowerCase().includes(q) ||
        (doc.tags || []).some((tag) => tag.toLowerCase().includes(q))
    );
  }, [docs, query, canViewAdminDocs]);

  // Create row: always visible as a hint; only acts when the user typed a new name
  const trimmedQuery = query.trim();
  const nameTaken =
    !!trimmedQuery &&
    !!docs &&
    docs.some(
      (doc) => (doc.title || '').trim().toLowerCase() === trimmedQuery.toLowerCase()
    );
  const canCreate = !!trimmedQuery && !nameTaken;
  const showCreateRow = docs !== null && !loadError;
  const createIndex = results.length;
  const optionCount = results.length + (showCreateRow ? 1 : 0);

  const handleCreate = async () => {
    if (creating || !canCreate) return;
    setCreating(true);
    setCreateError('');
    try {
      const doc = await SopAPI.create({ title: trimmedQuery });
      // Every useDocs instance (mission view, Docs area) refreshes its list
      // so the new doc resolves in tabs/pickers immediately
      window.dispatchEvent(new CustomEvent('fokus:docs-changed'));
      onPick(doc);
    } catch (e) {
      setCreateError(e.message || 'Failed to create doc');
      setCreating(false);
    }
  };

  useEffect(() => setSelectedIndex(0), [query]);

  useEffect(() => {
    inputRef.current?.focus({ preventScroll: true });
  }, []);

  // Keep the menu inside the visible doc surface (re-measure as results load)
  useEffect(() => {
    const menu = menuRef.current;
    if (!menu) return undefined;
    const apply = () => {
      setAdjustedPosition(clampFloatingMenuPosition(menu, position));
    };
    apply();
    const raf = requestAnimationFrame(apply);
    const ro = new ResizeObserver(apply);
    ro.observe(menu);
    window.addEventListener('resize', apply);
    window.addEventListener('scroll', apply, true);
    return () => {
      cancelAnimationFrame(raf);
      ro.disconnect();
      window.removeEventListener('resize', apply);
      window.removeEventListener('scroll', apply, true);
    };
  }, [position, results, showCreateRow, docs, loadError, createError, creating]);

  const handleKeyDown = (e) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setSelectedIndex((prev) => (optionCount ? (prev + 1) % optionCount : 0));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setSelectedIndex((prev) =>
        optionCount ? (prev - 1 + optionCount) % optionCount : 0
      );
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (results[selectedIndex]) onPick(results[selectedIndex]);
      else if (showCreateRow && selectedIndex === createIndex && canCreate) handleCreate();
    }
  };

  // Capture phase — wins over doc-sheet / mission Esc handlers
  useEffect(() => {
    const onKeyDown = (e) => {
      if (e.key !== 'Escape') return;
      e.preventDefault();
      e.stopPropagation();
      onClose();
    };
    window.addEventListener('keydown', onKeyDown, true);
    return () => window.removeEventListener('keydown', onKeyDown, true);
  }, [onClose]);

  // Close on click outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (menuRef.current && !menuRef.current.contains(e.target)) onClose();
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [onClose]);

  const renderList = () => {
    if (docs === null) {
      return <div className="doc-slash-empty">Loading docs…</div>;
    }
    if (loadError) {
      return <div className="doc-slash-empty">{loadError}</div>;
    }

    const rows = results.map((doc, index) => (
      <div
        key={doc.id}
        className={`doc-slash-item ${index === selectedIndex ? 'selected' : ''}`}
        onClick={(e) => {
          e.stopPropagation();
          onPick(doc);
        }}
        onMouseEnter={() => setSelectedIndex(index)}
      >
        <span className="doc-slash-icon">{mentionIconText(doc.icon)}</span>
        <span className="doc-slash-label">{doc.title || 'Untitled Doc'}</span>
        {doc.tags?.length > 0 && (
          <span className="doc-slash-description">· {doc.tags.join(', ')}</span>
        )}
      </div>
    ));

    if (docs.length === 0 && !trimmedQuery) {
      rows.push(
        <div key="__empty-hint" className="doc-slash-empty">
          No docs yet — type a name above to create one
        </div>
      );
    } else if (results.length === 0 && trimmedQuery && nameTaken) {
      rows.push(
        <div key="__no-match" className="doc-slash-empty">
          A doc with that name already exists
        </div>
      );
    } else if (results.length === 0 && trimmedQuery) {
      rows.push(
        <div key="__no-match" className="doc-slash-empty">
          No matches
        </div>
      );
    }

    if (showCreateRow) {
      rows.push(
        <div
          key="__create"
          className={`doc-slash-item doc-slash-item--create${
            !canCreate ? ' doc-slash-item--create-hint' : ''
          } ${selectedIndex === createIndex && canCreate ? 'selected' : ''}`}
          onClick={(e) => {
            e.stopPropagation();
            if (canCreate) handleCreate();
          }}
          onMouseEnter={() => canCreate && setSelectedIndex(createIndex)}
        >
          <span className="doc-slash-icon">+</span>
          <span className="doc-slash-label">
            {creating ? (
              <>Creating &ldquo;{trimmedQuery}&rdquo;&hellip;</>
            ) : (
              <>
                Create New:
                {trimmedQuery ? (
                  <span className="doc-slash-item__create-name"> {trimmedQuery}</span>
                ) : (
                  <span className="doc-slash-item__create-placeholder"> type a name above</span>
                )}
              </>
            )}
          </span>
        </div>
      );
    }

    if (createError) {
      rows.push(
        <div key="__create-error" className="doc-slash-empty doc-slash-empty--error">
          {createError}
        </div>
      );
    }

    return rows;
  };

  return createPortal(
    <div
      ref={menuRef}
      className="doc-slash-menu doc-mention-picker"
      style={{ top: adjustedPosition.y, left: adjustedPosition.x }}
    >
      <input
        ref={inputRef}
        className="doc-mention-picker__search"
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        onKeyDown={handleKeyDown}
        placeholder="Search docs…"
        aria-label="Search docs"
      />
      {renderList()}
      <div className="doc-mention-picker__hint">
        <span>↑↓ choose</span>
        <span>Enter insert</span>
        <span>Esc cancel</span>
      </div>
    </div>,
    document.body
  );
};
